import { debounce } from 'lodash';
import pluralize from 'pluralize';
import { snapshot } from 'valtio';
import { AppPages, currentPage } from '../../common/routing/app-navigator';
import { getAppOwnersStore } from '../../application-owners/api/applications-owners-api';
import { createSimpleAsyncAction } from 'async-utils';
import { AppEvents } from 'common-events';
import { logger } from 'logging-utils';
import InventoryFiltersStore from '../../dashboard-inventory/stores/inventory-filters-store';
import exclusionsService from '../../exclusions/services';
import { Relevance } from '../../irrelevant-apps/irrelevant-apps-types';
import ScanStore from '../../new-scan/store/scan-store';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import { trackAppcuesEvent } from '../../telemetry/appcues';
import applicationsService from '../services';
import ApplicationsFiltersStore from '../stores/applications-filters-store';
import {
  setMaxBuisnessPriority,
  setMaxRiskScore,
  setSliderBuisnessPriority,
  setSliderRiskScore,
  setTotalAppsFilters,
} from '../stores/applications-filters-store-actions';
import ApplicationsStore from '../stores/applications-store';
import {
  selectApplication,
  setApplications,
  setApplicationsOffset,
  setApplicationsTotal,
  setIrrelevantApplicationsTotal,
  setSearchValue,
} from '../stores/applications-store-actions';
import { getSelectedAppsIDs } from '../utils/application-utils';
import { loadDashboardInfo } from '../../executive-summary/actions/executive-summary-actions';

export type LoadApplicationsParams = {
  update?: boolean;
  cache?: boolean;
};

export const loadApplicationsIds = async () => {
  const { scanID, isScanning, realScanId } = snapshot(ScanStore);
  const { filters } = snapshot(InventoryFiltersStore);
  const { displaySelectedAppOwners } = getAppOwnersStore();
  const { allSelected } = snapshot(ApplicationsStore);

  const response = await applicationsService.getApplicationsIds.execute(
    {
      isAppIdOnly: true,
      search: ApplicationsStore.appFilterValue,
      applicationFilters: filters,
      offset: 0,
      ...(realScanId ? { scanId: realScanId } : scanID && { scanId: scanID }),
      owners: displaySelectedAppOwners,
      filters: ApplicationsFiltersStore.filterBy,
    },
    !isScanning,
  );

  if (response && response.applications) {
    ApplicationsStore.selected = response.applications.reduce(
      (acc, { appId }) => {
        acc[appId] = allSelected;
        return acc;
      },
      {},
    );
  }
};

export const loadApplications = createSimpleAsyncAction(
  async (params?: LoadApplicationsParams) => {
    const { update = false, cache = true } = params || {};
    const { isScanning } = snapshot(ScanStore);
    if (update) {
      loadApplicationsIds();
    }

    if (update) ApplicationsStore.offset = 0;

    const {
      total,
      offset,
      orderField,
      appFilterValue,
      orderDirection,
      orderByCategory,
      selectedAppId,
    } = snapshot(ApplicationsStore);

    if (total < offset) return;

    const columnKey = orderField;
    const { filters } = snapshot(InventoryFiltersStore);
    const { displaySelectedAppOwners } = getAppOwnersStore();
    const { scanID, realScanId } = snapshot(ScanStore);
    const { filterBy } = snapshot(ApplicationsFiltersStore);
    const response = await applicationsService.getApplications.execute(
      {
        offset: offset,
        owners: displaySelectedAppOwners,
        search: appFilterValue,
        limit: 100,
        applicationFilters: filters,
        ...(realScanId ? { scanId: realScanId } : scanID && { scanId: scanID }),
        orderBy: {
          direction: orderDirection,
          field: columnKey,
          category: orderByCategory,
        },
        filters: filterBy,
      },
      cache === false || !isScanning,
    );

    if (response && response.applications) {
      const {
        offset,
        applications,
        total,
        totalFilteredApps,
        totalIrrelevantApps,
      } = response;

      loadDashboardInfo();
      const isAppPage = currentPage() === AppPages.Applications;

      if (applications.length && !selectedAppId && isAppPage) {
        selectApplication(applications[0].appId);
      }
      if (applications.length > 0 && isAppPage) {
        trackAppcuesEvent(AppEvents.Apps.Loaded);
      }
      setApplicationsOffset(offset);
      setApplications(applications, update);
      setApplicationsTotal(total);
      setIrrelevantApplicationsTotal(totalIrrelevantApps);
      setTotalAppsFilters(totalFilteredApps);
    }
  },
  {
    asyncState: ApplicationsStore.loading,
    errorMessage: 'Failed to load applications',
  },
);

export const selectApp = (appId: string) => {
  const { selected } = snapshot(ApplicationsStore);
  ApplicationsStore.selected[appId] = !selected[appId];
};

export const setBusinessPriority = async (value: number, ids?: string[]) => {
  const appIds = ids || getSelectedAppsIDs();
  const { selected } = snapshot(ApplicationsStore);
  try {
    const response = await applicationsService.setPriority.execute({
      priority: value,
      appId: appIds,
    });
    if (response) {
      const newApps = ApplicationsStore.applications?.map(app =>
        selected[app.appId] ? { ...app, businessPriority: value } : app,
      );
      ApplicationsStore.applications = newApps;
      openSnackbar(
        `Business priority  for ${pluralize(
          'apps',
          appIds.length,
          true,
        )}  was changed succefully`,
        {
          variant: 'success',
        },
      );
    }
  } catch (e) {
    logger.error(e);
    openSnackbar(`Failed to change business priority`, {
      variant: 'error',
    });
    return false;
  }
};

export const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setSearchValue(e.target.value);
  onSearch();
};
export const onSearch = debounce(() => {
  loadApplications({ update: true });
}, 500);

export const setIrrelevant = async () => {
  try {
    const { selected, applications } = snapshot(ApplicationsStore);
    const input: { appId: string; appName: string; comment?: string }[] = [];
    applications?.forEach(app => {
      if (selected[app.appId]) {
        input.push({
          appId: app.appId,
          appName: app.appName,
          comment: app.exclusionComment,
        });
      }
    });
    const response = await exclusionsService.excludeApplications.execute(input);
    if (response.exclusions?.length) {
      openSnackbar('Apps were set irrelevant succesfully', {
        variant: 'success',
      });
      loadApplications({ update: true, cache: false });
    }
  } catch (e) {
    logger.error(e);
    openSnackbar('Failed to make the application irrelevant', {
      variant: 'error',
    });
  }
};

export const initApplication = async () => {
  const results = await applicationsService.getApplications.execute({
    offset: ApplicationsStore.offset,
    limit: 1,
  });
  if (results) {
    if (
      results.applications &&
      results.applications.length > 0 &&
      results.applications[0] &&
      results.applications[0]?.risk &&
      results.applications[0]?.businessPriority
    ) {
      setMaxRiskScore(results.applications[0].risk);
      setSliderRiskScore([0, results.applications[0].risk]);
      setMaxBuisnessPriority(results.applications[0].businessPriority);
      setSliderBuisnessPriority([0, results.applications[0].businessPriority]);
    } else {
      setMaxRiskScore(100);
      setSliderRiskScore([0, 100]);
      setMaxBuisnessPriority(100);
      setSliderBuisnessPriority([0, 100]);
    }
  }
};

export const setAppRelevance = async (appId: string, relevance: Relevance) => {
  const response = await applicationsService.setRelevance.execute({
    appIds: [appId],
    relevance,
  });

  openSnackbar(
    response ? 'Relevance was changed!' : 'Failed to change relevance',
    { variant: response ? 'success' : 'error' },
  );

  return response;
};
